<template>
  <v-dialog :value="active" :lazy="true" persistent max-width="600px">
    <v-card >
      <v-form ref="editForm" @submit.prevent="mode === 'edit' ? update() : add()">
        <v-toolbar card color="primary" dark dense>
          <v-spacer></v-spacer>
          <v-toolbar-title v-if="mode === 'edit'">{{$t('users.updateTitle')}}</v-toolbar-title>
          <v-toolbar-title v-else>{{$t('users.addTitle')}}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text v-if="mode === 'edit' && currentModel == null" class="text-xs-center">
          <v-progress-circular
            :width="6"
            :size="60"
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-text>
        <v-card-text v-else>
          <v-container grid-list-md>
            <v-layout wrap >
              <v-flex xs12>
                <v-text-field
                  :label="$t('users.lastname')"
                  v-model="currentModel.lastname"
                  :rules="[v => !!v || $t('fields.required')]"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  :label="$t('users.firstname')"
                  v-model="currentModel.firstname"
                  :rules="[v => !!v || $t('fields.required')]"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  readonly
                  :label="$t('users.login')"
                  v-model="currentModel.login"
                  :rules="[v => !!v || $t('fields.required')]"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-select
                  :items="filteredUsersRoles"
                  item-text="name"
                  item-value="number"
                  v-model="currentModel.role"
                  :label="$t('users.role')"
                  :rules="[rules.required]"
                  :item-disabled="(role) => role.number > user.role"
                ></v-select>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="newPassword"
                  :append-icon="show ? 'visibility_off' : 'visibility'"
                  :rules="mode === 'edit' ? [rules.min] : [rules.required, rules.min]"
                  :type="show ? 'text' : 'password'"
                  :label="$t('users.password')"
                  :hint="$t('users.passwordRule')"
                  counter
                  @click:append="show = !show"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="newPassword2"
                  :append-icon="show ? 'visibility_off' : 'visibility'"
                  :rules="mode === 'edit' ? [rules.min, rules.same] : [rules.required, rules.min, rules.same]"
                  :type="show ? 'text' : 'password'"
                  :label="$t('users.confirmation')"
                  :hint="$t('users.passwordRule')"
                  counter
                  @click:append="show = !show"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-select
                  :items="langsList"
                  v-model="currentModel.lang"
                  :label="$t('users.lang')"
                  :rules="[rules.required]"
                ></v-select>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="mode === 'edit'"
            color="primary"
            :loading="usersLoading"
            submit
            flat
            type="submit"
          >{{$t('general.update')}}</v-btn>
          <v-btn
            v-else
            color="primary"
            :loading="usersLoading"
            type="submit"
            flat
          >{{$t('general.add')}}</v-btn>
          <v-btn
            color="secondary"
            :disabled="usersLoading"
            submit
            flat
            @click.native="$emit('closedialog')"
          >{{$t('general.close')}}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {

  name: "UpdateUser",

  props: { active: Boolean, id: {}, mode: String },
  
  data() {
    return {
      show: false,
      rules: {
        required: value => !!value || this.$t('fields.required'),
        min: v => {
          if (v !== undefined && v.length !== 0) {
            return v.length >= 8 || this.$t('users.passwordRule')
          } else {
            return true;
          }
        },
        same: v =>
          v === this.newPassword || this.$t('users.passwordRule')
      },
      currentModel: {},
      newPassword: "",
      newPassword2: ""
    };
  },

  methods: {

    async init() {
      await this.$nextTick()
      this.$refs.editForm.reset();
      if (this.mode === 'edit') {
        this.getModel()
      } else {
        this.currentModel = Object.assign({}, this.userModel)
      }
    },

    async add() {
      if (this.$refs.editForm.validate()) {
        this.currentModel.password = this.newPassword;
        if (await this.addUser(this.currentModel)){
          this.$emit('closedialog')
        }
      }
    },

    async update() {
      if (this.$refs.editForm.validate()) {
        this.currentModel.password = this.newPassword;
        if (
          await this.updateUser({
            id: this.id,
            data: this.currentModel
          })
        ) {
          this.$emit("closedialog");
        }
      }
    },

    async getModel() {
      this.currentModel = null
      let model = await this.getOneUser(this.id);
      this.currentModel = Object.assign({}, model);
      this.currentModel.password = "";
    },

    setLogin() {
      if (this.currentModel.firstname !== undefined) {
        this.currentModel.login = (
          this.currentModel.firstname.charAt(0) + this.currentModel.lastname
        ).toLowerCase();
      }
    },

    ...mapActions('users', {
      getOneUser: 'getOne',
      updateUser: 'update',
      addUser: 'add'
    })

  },

  computed: {

    filteredUsersRoles () {
      return this.usersRoles.filter((role) => {
        if (this.user.role < 3 && role.number === 3) return false
        return true
      })
    },

    ...mapGetters('users', {
      usersRoles: 'roles',
      usersLoading: 'loading',
      userModel: 'model'
    }),

    ...mapGetters('user', {
      user: 'current'
    }),

    ...mapGetters('interfaceSettings', {
      langsList: 'adminLangs'
    })
  },

  watch: {

    active(val) {
      if (val) {
        this.init()
      }
    },

    "currentModel.firstname"(val) {
      if (val !== undefined) {
        this.setLogin();
      }
    },

    "currentModel.lastname"(val) {
      if (val !== undefined) {
        this.setLogin();
      }
    }

  }
};
</script>
